import React, { useState, useEffect } from "react";
import API_BASE_URL from 'config/api';
import {
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { BlueButton, WhiteButton } from "styles";
import dayjs from "dayjs";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DownloadIcon from "@mui/icons-material/Download";
import PaymentIcon from "@mui/icons-material/Payment";
import MDBadge from "components/MDBadge";

const USER_DATA_KEY = "userMeData";

const InvoiceSettings = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Tarih formatlama
  const formatDate = (dateString) => {
    return dayjs(dateString).format("DD.MM.YYYY HH:mm");
  };

  // Para birimi formatlama
  const formatCurrency = (amount, currency = "TRY") => {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency,
    }).format(amount);
  };

  // Ödeme yöntemi çevirici
  const getPaymentMethodText = (method) => {
    switch (method) {
      case "card":
        return "Kredi Kartı";
      case "bank_transfer":
        return "Banka Transferi";
      case "paypal":
        return "PayPal";
      default:
        return method;
    }
  };

  // Durum çevirici
  const getStatusText = (status) => {
    switch (status) {
      case "success":
        return "Başarılı";
      case "failed":
        return "Başarısız";
      default:
        return status;
    }
  };

  // Durum rengi
  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "success";
      case "failed":
        return "error";
      default:
        return "warning";
    }
  };

  // Session storage'dan kullanıcı verisini al
  const getCachedUserData = () => {
    const cachedData = sessionStorage.getItem(USER_DATA_KEY);
    return cachedData ? JSON.parse(cachedData) : null;
  };

  // Kullanıcı verisini yeniden yükle
  const reloadUserData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/me`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      });
      const data = await response.json();
      if (response.ok && data.user) {
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(data));
        setUserData(data);
      } else {
        throw new Error("Kullanıcı verisi alınamadı");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const cachedUser = getCachedUserData();
    if (cachedUser) {
      setUserData(cachedUser);
      setLoading(false);
      return;
    }
    reloadUserData();
  }, []);

  // İrsaliye PDF oluşturma
  const generateInvoicePDF = async (payment) => {
    try {
      setPdfLoading(true);

      // Görünmez bir div oluştur
      const element = document.createElement("div");
      element.style.position = "absolute";
      element.style.left = "-9999px";
      element.style.width = "210mm";
      element.style.padding = "20mm";
      element.style.fontFamily = "Arial, sans-serif";
      element.style.backgroundColor = "white";

      // İrsaliye HTML şablonu
      element.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #2c3e50; font-size: 24px; margin-bottom: 10px;">İRSALİYE</h1>
          <div style="display: flex; justify-content: space-between; margin-top: 30px;">
            <div style="text-align: left;">
              <p><strong>İrsaliye No:</strong> ${payment.paymentId}</p>
              <p><strong>Tarih:</strong> ${formatDate(payment.createdAt)}</p>
            </div>
            <div style="text-align: right;">
              <p><strong>Domain:</strong> ${payment.domain}</p>
            </div>
          </div>
        </div>

        <div style="display: flex; justify-content: space-between; margin: 30px 0;">
          <div style="width: 48%;">
            <h3 style="border-bottom: 1px solid #ddd; padding-bottom: 5px;">SATICI</h3>
            <p><strong>Logicookie</strong></p>
            <p>Vergi No: 38002668508</p>
          </div>
          <div style="width: 48%;">
            <h3 style="border-bottom: 1px solid #ddd; padding-bottom: 5px;">ALICI</h3>
            <p><strong>${payment.billingInfo?.name || "-"}</strong></p>
            <p>${payment.billingInfo?.address || "-"}</p>
            <p>${payment.billingInfo?.city || "-"} / ${
        payment.billingInfo?.country || "-"
      }</p>
            <p>Vergi No: ${payment.billingInfo?.taxNumber || "-"}</p>
          </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <thead>
            <tr style="background-color: #f5f5f5;">
              <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Ürün/Hizmet</th>
              <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Tutar</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding: 10px; border: 1px solid #ddd;">${
                payment.subscription?.plan?.name || "Abonelik"
              }</td>
              <td style="padding: 10px; text-align: right; border: 1px solid #ddd;">${formatCurrency(
                payment.subscription?.price
              )}</td>
            </tr>
            <tr style="font-weight: bold;">
              <td style="padding: 10px; text-align: right; border: 1px solid #ddd;">TOPLAM</td>
              <td style="padding: 10px; text-align: right; border: 1px solid #ddd;">${formatCurrency(
                payment.subscription?.price
              )}</td>
            </tr>
          </tbody>
        </table>

        <div style="margin-top: 50px;">
          <p><strong>Ödeme Yöntemi:</strong> ${getPaymentMethodText(
            payment.purchaseMethod
          )}</p>
          <p><strong>Durum:</strong> ${getStatusText(payment.status)}</p>
        </div>

        <div style="margin-top: 50px; text-align: center; font-size: 12px; color: #777;">
          <p>Teşekkür ederiz</p>
          <p>${formatDate(payment.createdAt)} tarihinde ödenmiştir</p>
        </div>
      `;

      document.body.appendChild(element);

      // HTML'i canvas'a dönüştür
      const canvas = await html2canvas(element, {
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      // PDF oluştur
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`fatura-${payment.paymentId}.pdf`);
      setSuccessMsg("İrsaliye başarıyla indirildi");
    } catch (error) {
      console.error("PDF oluşturma hatası:", error);
      setErrorMsg("İrsaliye oluşturulurken hata oluştu");
    } finally {
      setPdfLoading(false);
      // Geçici elementi temizle
      const elements = document.querySelectorAll("div[style*='left: -9999px']");
      elements.forEach((el) => el.remove());
    }
  };

  // Tüm paymentLog'ları topla
  const allPaymentLogs = [];
  userData?.user?.clients?.forEach((client) => {
    client.sites?.forEach((site) => {
      if (site.subscription?.paymentLogs) {
        site.subscription.paymentLogs.forEach((log) => {
          allPaymentLogs.push({
            ...log,
            domain: site.domain,
            billingInfo: site.billingInfo,
            subscription: site.subscription,
            purchaseMethod: site.subscription.purchaseMethod,
          });
        });
      }
    });
  });

  if (loading) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <Typography color="error">{error}</Typography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Ödeme Geçmişi
                </Typography>
                {allPaymentLogs.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead
                        sx={{
                          bgcolor: "grey.100",
                          display: "table-header-group",
                        }}
                      >
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Domain
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Ödeme ID
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Durum
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Tutar
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Yöntem
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Tarih
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            İrsaliye
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allPaymentLogs.map((payment) => (
                          <TableRow key={payment.paymentId}>
                            <TableCell>{payment.domain}</TableCell>
                            <TableCell>{payment.paymentId}</TableCell>
                            <TableCell>
                              <MDBadge
                                variant="contained"
                                badgeContent={getStatusText(payment.status)}
                                color={getStatusColor(payment.status)}
                                container
                              />
                            </TableCell>
                            <TableCell>
                              {formatCurrency(payment.subscription?.price)}
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <PaymentIcon fontSize="small" />
                                {getPaymentMethodText(payment.purchaseMethod)}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {formatDate(payment.createdAt)}
                            </TableCell>
                            <TableCell>
                              <BlueButton
                                startIcon={<ReceiptIcon />}
                                onClick={() => setSelectedPayment(payment)}
                                size="small"
                              >
                                Görüntüle
                              </BlueButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ mt: 2, textAlign: "center", py: 4 }}
                  >
                    Henüz ödeme geçmişi bulunmamaktadır
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* İrsaliye Detay Modalı */}
      <Dialog
        open={!!selectedPayment}
        onClose={() => setSelectedPayment(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon />
            İrsaliye Detayları - {selectedPayment?.paymentId}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: "grey.100" }}>
                      <TableRow>
                        <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
                          Ödeme Bilgileri
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell width="30%" sx={{ fontWeight: "bold" }}>
                          Domain
                        </TableCell>
                        <TableCell>{selectedPayment.domain}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Ödeme ID
                        </TableCell>
                        <TableCell>{selectedPayment.paymentId}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Durum</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusText(selectedPayment.status)}
                            color={getStatusColor(selectedPayment.status)}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Tutar</TableCell>
                        <TableCell>
                          {formatCurrency(selectedPayment.subscription?.price)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Ödeme Yöntemi
                        </TableCell>
                        <TableCell>
                          {getPaymentMethodText(selectedPayment.purchaseMethod)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Tarih</TableCell>
                        <TableCell>
                          {formatDate(selectedPayment.createdAt)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead sx={{ bgcolor: "grey.100" }}>
                      <TableRow>
                        <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
                          İrsaliye Bilgileri
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell width="30%" sx={{ fontWeight: "bold" }}>
                          Ad Soyad/Ünvan
                        </TableCell>
                        <TableCell>
                          {selectedPayment.billingInfo?.name || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Adres</TableCell>
                        <TableCell>
                          {selectedPayment.billingInfo?.address || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Şehir</TableCell>
                        <TableCell>
                          {selectedPayment.billingInfo?.city || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>İlçe</TableCell>
                        <TableCell>
                          {selectedPayment.billingInfo?.district || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Ülke</TableCell>
                        <TableCell>
                          {selectedPayment.billingInfo?.country || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Posta Kodu
                        </TableCell>
                        <TableCell>
                          {selectedPayment.billingInfo?.postalCode || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Vergi Dairesi
                        </TableCell>
                        <TableCell>
                          {selectedPayment.billingInfo?.taxOffice || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Vergi No/TCKN
                        </TableCell>
                        <TableCell>
                          {selectedPayment.billingInfo?.taxNumber || "-"}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <WhiteButton onClick={() => setSelectedPayment(null)}>
            Kapat
          </WhiteButton>
          <BlueButton
            startIcon={<DownloadIcon />}
            onClick={() => generateInvoicePDF(selectedPayment)}
            disabled={pdfLoading}
          >
            {pdfLoading ? <CircularProgress size={24} /> : "İrsaliye PDF İndir"}
          </BlueButton>
        </DialogActions>
      </Dialog>

      <Footer />

      {/* Bildirimler */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={6000}
        onClose={() => setSuccessMsg("")}
      >
        <Alert severity="success" onClose={() => setSuccessMsg("")}>
          {successMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={6000}
        onClose={() => setErrorMsg("")}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default InvoiceSettings;
